import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { SKILLS } from "../../data/portfolioData";
import Reveal from "../ui/Reveal";

const DEVICON = "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons";
const SIMPLE = "https://cdn.simpleicons.org";

// Line icons for concepts and tools that don't have a public logo.
const GLYPHS = {
  rag: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <circle cx="11" cy="13" r="2.5" />
      <path d="m13 15 2.5 2.5" />
    </>
  ),
  tune: (
    <>
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="17" r="2" />
    </>
  ),
  nlp: (
    <>
      <path d="M4 5h16v10H9l-4 4v-4H4z" />
      <path d="M8 9h8M8 12h5" />
    </>
  ),
  brain: (
    <>
      <path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 6 1V6a2 2 0 0 0-3-2z" />
      <path d="M15 4a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-6 1" />
      <path d="M12 9h2M10 13h2" />
    </>
  ),
  // low-rank adaptation: a big matrix ≈ two thin ones
  lora: (
    <>
      <rect x="3" y="5" width="8" height="14" rx="1.2" />
      <path d="M13.5 12h1.5" />
      <rect x="17" y="5" width="2.5" height="14" rx="1" />
      <rect x="14" y="16.5" width="8" height="2.5" rx="1" />
    </>
  ),
  // agent ↔ environment loop
  rl: (
    <>
      <path d="M20 12a8 8 0 0 1-14.3 4.9" />
      <path d="M4 12a8 8 0 0 1 14.3-4.9" />
      <path d="M18.5 3.5v3.8h-3.8M5.5 20.5v-3.8h3.8" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  // stacked encoder blocks
  transformer: (
    <>
      <rect x="5" y="3" width="14" height="5" rx="1.5" />
      <rect x="5" y="10" width="14" height="5" rx="1.5" />
      <rect x="5" y="17" width="14" height="4" rx="1.5" />
      <path d="M12 8v2M12 15v2" />
    </>
  ),
  // focus target
  attention: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2" />
    </>
  ),
  // points in vector space
  embeddings: (
    <>
      <path d="M4 20V4M4 20h16" />
      <circle cx="9" cy="14" r="1.3" />
      <circle cx="11" cy="10" r="1.3" />
      <circle cx="16" cy="8" r="1.3" />
      <circle cx="17" cy="14" r="1.3" />
      <circle cx="13" cy="16" r="1.3" />
    </>
  ),
  // stacked convolution filters
  cnn: (
    <>
      <rect x="3" y="7" width="10" height="10" rx="1" />
      <rect x="7" y="4" width="10" height="10" rx="1" />
      <rect x="11" y="10" width="10" height="10" rx="1" />
    </>
  ),
  // generator vs discriminator
  gan: (
    <>
      <circle cx="6" cy="12" r="3" />
      <rect x="15" y="9" width="6" height="6" rx="1" />
      <path d="M9 10.5h5M14 13.5H9" />
      <path d="m12.5 9 1.5 1.5-1.5 1.5M10.5 12 9 13.5l1.5 1.5" />
    </>
  ),
  // U-shaped encoder/decoder
  unet: (
    <>
      <path d="M4 4v10a8 8 0 0 0 16 0V4" />
      <path d="M8 4v9a4 4 0 0 0 8 0V4" />
      <path d="M8 9h8" strokeDasharray="1.5 2" />
    </>
  ),
  // compound scaling: growing bars
  efficientnet: (
    <>
      <path d="M4 20h16" />
      <rect x="5" y="14" width="3" height="6" rx="0.8" />
      <rect x="10.5" y="10" width="3" height="10" rx="0.8" />
      <rect x="16" y="5" width="3" height="15" rx="0.8" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
      <path d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3 9 5-9 5-9-5z" />
      <path d="m3 13 9 5 9-5" />
    </>
  ),
  api: (
    <>
      <path d="M8 7 3 12l5 5M16 7l5 5-5 5" />
      <path d="m14 4-4 16" />
    </>
  ),
  apps: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="3.5" />
    </>
  ),
};

const SkillIcon = ({ icon, name }) => {
  const [failed, setFailed] = useState(false);
  const [kind, slug] = icon.split(":");

  if (kind === "glyph" || failed) {
    return (
      <svg
        viewBox="0 0 24 24"
        className="w-5 h-5 text-accent"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {GLYPHS[slug] || GLYPHS.layers}
      </svg>
    );
  }

  const src = kind === "devicon" ? `${DEVICON}/${slug}/${slug}-original.svg` : `${SIMPLE}/${slug}`;
  // Some brand marks are black; a light backing keeps them visible in dark mode.
  return (
    <span className="flex items-center justify-center w-6 h-6 rounded-md dark:bg-[#f3e8de] dark:p-0.5">
      <img src={src} alt="" className="w-5 h-5 object-contain" loading="lazy" onError={() => setFailed(true)} title={name} />
    </span>
  );
};

const ALL = "All";

// First reveal: chips ripple in from the middle of the list outward.
// Tab switches afterwards use a quick left-to-right pop.
const chipVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.6, filter: "blur(6px)" },
  show: ({ delay }) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 260, damping: 20, delay },
  }),
  exit: { opacity: 0, scale: 0.8, filter: "blur(4px)", transition: { duration: 0.15 } },
};

const Skills = () => {
  const tabs = [ALL, ...SKILLS.categories.map((c) => c.name)];
  const [active, setActive] = useState(ALL);
  const gridRef = useRef(null);
  const inView = useInView(gridRef, { once: true, amount: 0.3 });
  const [entered, setEntered] = useState(false);

  // Once the ripple has played, later renders use the quicker tab-switch timing.
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setEntered(true), 1400);
    return () => clearTimeout(t);
  }, [inView]);

  const items = useMemo(
    () =>
      active === ALL
        ? SKILLS.categories.flatMap((c) => c.items)
        : SKILLS.categories.find((c) => c.name === active)?.items || [],
    [active]
  );

  return (
    <section id="skills" className="pt-16 pb-10">
      <Reveal className="text-center">
        <h2 className="heading">
          <span className="text-accent">Skills</span> & Tools
        </h2>
        <p className="text-ink/65 max-w-2xl mx-auto mt-5 leading-relaxed text-sm md:text-base">{SKILLS.intro}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 flex justify-center">
          <div
            role="tablist"
            aria-label="Skill categories"
            className="flex gap-1 md:gap-2 overflow-x-auto border-b border-ink/15 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={active === tab}
                onClick={() => setActive(tab)}
                className={`relative whitespace-nowrap px-4 py-3 text-sm font-semibold transition-colors ${
                  active === tab ? "text-accent" : "text-ink/70 hover:text-ink"
                }`}
              >
                {tab}
                {active === tab && (
                  <motion.span
                    layoutId="skills-tab"
                    className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full bg-accent"
                    transition={{ type: "spring", stiffness: 420, damping: 34 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      <motion.ul ref={gridRef} layout className="mt-10 flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
        <AnimatePresence mode="popLayout" initial={false}>
          {items.map((skill, i) => {
            const mid = (items.length - 1) / 2;
            const delay = entered ? Math.min(i, 14) * 0.025 : 0.15 + Math.abs(i - mid) * 0.045;
            return (
              <motion.li
                key={skill.name}
                layout
                custom={{ delay }}
                variants={chipVariants}
                initial="hidden"
                animate={inView ? "show" : "hidden"}
                exit="exit"
                whileHover={{ y: -4, transition: { type: "spring", stiffness: 400, damping: 20 } }}
                className="flex items-center gap-2.5 rounded-xl bg-card border border-ink/10 px-4 py-3 text-sm font-semibold text-ink shadow-[0_1px_0_rgb(0_0_0/0.02)] hover:border-accent/40 hover:shadow-[0_12px_28px_rgb(0_0_0/0.08)] transition-[border-color,box-shadow]"
              >
                <SkillIcon icon={skill.icon} name={skill.name} />
                {skill.name}
              </motion.li>
            );
          })}
        </AnimatePresence>
      </motion.ul>
    </section>
  );
};

export default Skills;
